import { createContext, useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Globe } from "lucide-react";
import { API } from "../App";

const Ctx = createContext({ lang: "en", setLang: () => {}, langs: [], langParam: "" });
const KEY = "wall_lang";

// Site language: "en" or one of the sister-city languages. Only pages an admin has translated change; the rest stay English.
export const LanguageProvider = ({ children }) => {
  const [lang, setLangState] = useState(() => localStorage.getItem(KEY) || "en");
  const [langs, setLangs] = useState([]);
  useEffect(() => { axios.get(`${API}/translate/languages`).then((r) => setLangs(r.data.languages || [])).catch(() => {}); }, []);
  useEffect(() => {
    const l = langs.find((x) => x.code === lang);
    document.documentElement.lang = lang;
    document.documentElement.dir = l?.rtl ? "rtl" : "ltr";
  }, [lang, langs]);
  const setLang = (l) => { localStorage.setItem(KEY, l); setLangState(l); };
  const value = useMemo(() => ({ lang, setLang, langs, langParam: lang === "en" ? "" : `lang=${encodeURIComponent(lang)}` }), [lang, langs]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
};

export const useLanguage = () => useContext(Ctx);

export const LanguagePicker = ({ className = "" }) => {
  const { lang, setLang, langs } = useLanguage();
  const [open, setOpen] = useState(false);
  const cur = langs.find((l) => l.code === lang);
  return (
    <div className={`relative ${className}`} data-testid="language-picker">
      <button type="button" onClick={() => setOpen((o) => !o)} className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-bold text-[#1A1A1A] hover:bg-black/5" aria-label="Choose language" title="Language" data-testid="language-picker-btn">
        <Globe className="h-4 w-4" /> <span className="hidden sm:inline">{cur ? cur.native : "English"}</span>
      </button>
      {open && (
        <div className="absolute right-0 mt-1 z-[120] w-56 max-h-[70vh] overflow-y-auto rounded-xl bg-white shadow-xl ring-1 ring-black/10 p-1" data-testid="language-picker-menu">
          <button type="button" onClick={() => { setLang("en"); setOpen(false); }} className={`block w-full text-left rounded-lg px-3 py-1.5 text-sm ${lang === "en" ? "bg-[#2E5C55] text-white" : "hover:bg-black/5"}`} data-testid="language-option-en">English</button>
          {langs.map((l) => (
            <button key={l.code} type="button" onClick={() => { setLang(l.code); setOpen(false); }} className={`block w-full text-left rounded-lg px-3 py-1.5 text-sm ${lang === l.code ? "bg-[#2E5C55] text-white" : "hover:bg-black/5"}`} data-testid={`language-option-${l.code}`}>
              {l.native} <span className="opacity-60 text-xs">· {l.name}</span>
            </button>
          ))}
          <p className="px-3 pt-2 pb-1 text-[10px] leading-snug text-[#5C5C5C]">Pages are translated one at a time by our team (machine-assisted, reviewed). Untranslated pages stay in English.</p>
        </div>
      )}
    </div>
  );
};

// Shown on a translated page: which language, how it was made, and a way back to English.
export const TranslationNotice = ({ meta }) => {
  const { setLang, langs } = useLanguage();
  if (!meta) return null;
  const l = langs.find((x) => x.code === meta.lang);
  return (
    <div className="max-w-6xl mx-auto px-4 md:px-12 pt-3" data-testid="translation-notice">
      <p className="rounded-full bg-[#2E5C55]/10 text-[#1F3F3A] px-4 py-1.5 text-xs font-semibold inline-flex items-center gap-2">
        <Globe className="h-3.5 w-3.5" /> {l ? l.native : meta.lang} · {meta.status === "reviewed" ? "reviewed translation" : `machine translation (${meta.engine === "deepl" ? "DeepL" : "Claude"}) — not yet reviewed`}
        <button type="button" onClick={() => setLang("en")} className="underline" data-testid="translation-notice-english">English</button>
      </p>
    </div>
  );
};
